<template>
	<section>
		<template v-if="commentCount">
			<hr class="mb-8 border-gray-500" />
			<header class="flex justify-between">
				<h3 class="mb-16 text-gray-400">{{ commentCount }} {{ commentCount > 1 ? 'Comments' : 'Comment' }}</h3>
				<BaseTooltip
					v-if="canUserEditSession || $store.hasUserProductViewerRole"
					:text="`${isUserSubscribedToSession ? 'Unsubscribe from' : 'Subscribe to'} updates of this session`"
					placement="left"
				>
					<button class="text-gray-400 hover:text-gray-100 p-8 -m-8" @click="toggleSessionNotifications">
						<BaseIcon v-if="isUserSubscribedToSession" name="mono/notify-on" />
						<BaseIcon v-else name="mono/notify-off" />
					</button>
				</BaseTooltip>
			</header>
			<ul :class="{'-mx-24': !$sessionLayout.devToolsHidden}" class="mb-20">
				<SessionComment
					v-for="comment in session.comments"
					:key="comment.id"
					data-test="comments-list-item"
					:comment="comment"
				/>
			</ul>
		</template>
		<!-- button and form for new comments  -->
		<template v-if="canUserEditSession || $store.hasUserProductViewerRole">
			<SessionCommentForm v-if="showForm" @close="showForm = false" />
			<button
				v-else
				data-test="post-comment-button"
				class="w-full p-12 text-left text-gray-350 border border-gray-550 rounded bg-white dark:bg-gray-650"
				:class="{'cursor-not-allowed': !isCommentsFeatureEnabled}"
				:disabled="!isCommentsFeatureEnabled"
				@click="showForm = true"
				@dragover.prevent="showForm = true"
			>
				<BaseTooltip
					v-if="!isCommentsFeatureEnabled"
					:text="'Upgrade to team plan to add comments'"
					:placement="'top-start'"
				>
					Post a comment
				</BaseTooltip>
				<span v-else>Post a comment</span>
			</button>
		</template>
	</section>
</template>

<script>
import {defineAsyncComponent} from 'vue'
import {storeToRefs} from 'pinia'
import {FEATURE_CODES} from 'shared/constants/featureCodes'

import SessionCommentForm from 'shared/components/SessionCommentForm.vue'
import insertSessionNotificationSubscription from 'shared/apollo/insertSessionNotificationSubscription'
import deleteSessionNotificationSubscription from 'shared/apollo/deleteSessionNotificationSubscription'
import analytics from 'shared/utils/analytics'
import {useSessionStore} from 'shared/stores/session'

export default {
	name: 'SessionComments',
	components: {
		SessionCommentForm,
		SessionComment: defineAsyncComponent(() => import('shared/components/SessionComment.vue')),
	},
	emits: ['refetchSession'],
	setup() {
		const sessionStore = useSessionStore()
		const {session, canUserEditSession} = storeToRefs(sessionStore)
		return {session, canUserEditSession}
	},
	data() {
		return {
			showForm: false,
			publicUsers: {},
			scrollToCommentId: null,
		}
	},
	computed: {
		isUserSubscribedToSession() {
			return Boolean(this.session.notificationSubscriptions?.length)
		},
		commentCount() {
			return this.session.comments.length
		},
		isCommentsFeatureEnabled() {
			return this.$store.hasPlanFeature(FEATURE_CODES.INCLUDE_COMMENTS)
		},
	},
	async mounted() {
		window.addEventListener('attachmentsUpdated', this.onAttachmentsUpdated)
	},
	unmounted() {
		window.removeEventListener('attachmentsUpdated', this.onAttachmentsUpdated)
	},
	methods: {
		toggleSessionNotifications() {
			analytics.track('Session Notifications Toggled', {
				enabled: !this.isUserSubscribedToSession,
			})
			if (this.isUserSubscribedToSession) {
				deleteSessionNotificationSubscription({
					sessionId: this.session.id,
					userId: this.$store.user.id,
				})
				return
			}
			insertSessionNotificationSubscription({
				sessionId: this.session.id,
				userId: this.$store.user.id,
			})
		},
		onAttachmentsUpdated() {
			this.$emit('refetchSession')
		},
	},
}
</script>
